<template>
  <div
    class="add-crypto-account-page"
    :class="$vuetify.theme.dark ? 'add-crypto-account--dark' : 'add-crypto-account--light'"
  >
    <div class="add-crypto-account-shell">
      <div class="add-crypto-account-heading">
        <span class="heading-icon"><v-icon>mdi-bitcoin</v-icon></span>
        <div>
          <h2>Add Crypto Account</h2>
          <p>Save a cryptocurrency network and wallet address.</p>
        </div>
      </div>

      <v-card class="crypto-account-panel" elevation="0">
        <div class="crypto-account-panel-title">
          <v-icon>mdi-wallet-plus-outline</v-icon>
          <div>
            <h3>Crypto Wallet Details</h3>
            <p>Enter the network and receiving wallet address.</p>
          </div>
        </div>
          <v-card
            class="deposit-form password-field create-account-form"
            color="transparent"
            elevation="0"
          >
            <v-row dense>
              <v-col cols="12" class="crypto-field-col">
                <fieldset>
                  <legend>Select Crypto Network</legend>
                  <v-select
                    v-model="formData.network"
                    solo
                    :items="networks"
                    item-text="network"
                    item-value="renamedNetwork"
                    append-icon="mdi-chevron-down"
                    label="Select One"
                    class="custom-select"
                    :error-messages="networkError"
                    @input="validateNetwork"
                  ></v-select>
                </fieldset>
              </v-col>
              <v-col cols="12" class="crypto-field-col">
                <fieldset>
                  <legend>Enter Your Crypto Address</legend>
                  <v-text-field
                    v-model="formData.address"
                    label="Enter your crypto address"
                    single-line
                    solo
                    :error-messages="addressError"
                    @input="validateAddress"
                    class="custom-select"
                  ></v-text-field>
                </fieldset>
              </v-col>
              <v-col cols="12">
                <div class="crypto-form-actions">
                  <v-btn
                    class="ma-0 text-capitalize btn-account"
                    color="greenMain"
                    tile
                    width="146px"
                    elevation="0"
                    :loading="loading"
                    :disabled="!isValid || loading"
                    @click="handleSubmit"
                  >
                    Save
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { CRYPTO__ACTIONS } from "@/store/action-types";

export default {
  data() {
    return {
      loading: false,
      formData: {
        network: "",
        address: "",
      },
      networkError: "",
      addressError: "",
    };
  },

  computed: {
    ...mapState("crypto", ["networks"]),
    isValid() {
      return (
        this.formData.network &&
        this.formData.address &&
        !this.networkError &&
        !this.addressError
      );
    },
  },

  methods: {
    ...mapActions("crypto", [CRYPTO__ACTIONS.CREATE, CRYPTO__ACTIONS.NETWORKS]),

    validateNetwork(value) {
      this.networkError = !value ? "Network is required" : "";
    },

    validateAddress(value) {
      if (!value) {
        this.addressError = "Address is required";
      }  else {
        this.addressError = "";
      }
    },

    async handleSubmit() {
      if (!this.isValid) return;

      this.loading = true;
      try {
        const submitData = {
          ...this.formData,
          crypto_type: this.formData.network, // Add crypto_type if needed
          status: 1, // Default active status
        };
        await this[CRYPTO__ACTIONS.CREATE](submitData);
        this.resetForm();
      } catch (error) {
        console.error("Error saving address:", error);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.formData = {
        network: "",
        address: "",
      };
      this.networkError = "";
      this.addressError = "";
    }
  },

  async mounted() {
    await this[CRYPTO__ACTIONS.NETWORKS]();
  },
};
</script>

<style scoped>
.add-crypto-account-page {
  --crypto-accent: #d99a00;
  --crypto-accent-soft: rgba(217, 154, 0, 0.12);
  --crypto-bg: #f7f8fa;
  --crypto-panel: #ffffff;
  --crypto-field: #f4f5f7;
  --crypto-border: #dfe2e6;
  --crypto-text: #171a1f;
  --crypto-muted: #626973;
  height: calc(100vh - 64px);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--crypto-bg);
  color: var(--crypto-text);
}
.add-crypto-account--dark {
  --crypto-accent: #ffc400;
  --crypto-accent-soft: rgba(255, 196, 0, 0.12);
  --crypto-bg: #030b13;
  --crypto-panel: #07111b;
  --crypto-field: #0b1621;
  --crypto-border: #293026;
  --crypto-text: #f4f5f7;
  --crypto-muted: #aeb6c0;
}
.add-crypto-account-shell {
  width: min(100%, 820px);
  padding: 24px 28px 48px;
}
.add-crypto-account-heading,
.crypto-account-panel-title {
  display: flex;
  align-items: center;
  gap: 14px;
}
.add-crypto-account-heading { margin-bottom: 20px; }
.heading-icon {
  display: grid;
  place-items: center;
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  border: 1px solid var(--crypto-accent);
  border-radius: 10px;
  background: var(--crypto-accent-soft);
}
.heading-icon .v-icon,
.crypto-account-panel-title > .v-icon { color: var(--crypto-accent) !important; }
.add-crypto-account-heading h2,
.crypto-account-panel-title h3 { margin: 0; color: var(--crypto-text); }
.add-crypto-account-heading h2 { font-size: 23px; }
.add-crypto-account-heading p,
.crypto-account-panel-title p { margin: 4px 0 0; color: var(--crypto-muted); font-size: 13px; }
.crypto-account-panel {
  padding: 24px;
  border: 1px solid var(--crypto-border) !important;
  border-radius: 10px !important;
  background: var(--crypto-panel) !important;
  color: var(--crypto-text) !important;
}
.crypto-account-panel-title {
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--crypto-border);
}
.crypto-account-panel-title > .v-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--crypto-accent-soft);
}
.crypto-account-panel-title h3 { font-size: 17px; }
.add-crypto-account-page ::v-deep .create-account-form {
  max-width: none !important;
  padding: 0 !important;
  background: transparent !important;
}
.add-crypto-account-page ::v-deep .row--dense > .col { padding: 7px 5px !important; }
.add-crypto-account-page ::v-deep .crypto-field-col + .crypto-field-col {
  margin-top: 10px;
}
.add-crypto-account-page ::v-deep fieldset {
  display: block !important;
  width: 100% !important;
  height: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
}
.add-crypto-account-page ::v-deep legend {
  display: block !important;
  float: none !important;
  position: static !important;
  width: auto !important;
  margin: 0 0 7px !important;
  padding: 0 !important;
  background: transparent !important;
  color: var(--crypto-text) !important;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}
.add-crypto-account-page ::v-deep .custom-select { margin: 0 !important; padding: 0 !important; }
.add-crypto-account-page ::v-deep .v-input__slot {
  min-height: 48px !important;
  margin: 0 !important;
  padding: 0 14px !important;
  border: 1px solid var(--crypto-border) !important;
  border-radius: 6px !important;
  background: var(--crypto-field) !important;
  box-shadow: none !important;
}
.add-crypto-account-page ::v-deep .v-input--is-focused .v-input__slot { border-color: var(--crypto-accent) !important; }
.add-crypto-account-page ::v-deep .v-input input,
.add-crypto-account-page ::v-deep .v-select__selection { color: var(--crypto-text) !important; font-size: 14px; }
.add-crypto-account-page ::v-deep .v-input input { min-height: 46px; padding: 0 !important; }
.add-crypto-account-page ::v-deep .v-label { color: var(--crypto-muted) !important; }
.add-crypto-account-page ::v-deep .v-icon { color: var(--crypto-accent) !important; }
.add-crypto-account-page ::v-deep .v-text-field__details {
  min-height: 15px;
  margin: 3px 0 0 !important;
  padding: 0 2px !important;
}
.crypto-form-actions { display: flex; margin-top: 6px; }
.add-crypto-account-page ::v-deep .btn-account {
  min-width: 146px;
  min-height: 42px;
  border-radius: 6px !important;
  background: var(--crypto-accent) !important;
  color: #101010 !important;
  font-weight: 700;
}
.add-crypto-account-page ::v-deep .btn-account.v-btn--disabled {
  background: var(--crypto-accent-soft) !important;
  color: var(--crypto-muted) !important;
}

@media (max-width: 600px) {
  .add-crypto-account-page { height: calc(100dvh - 56px); }
  .add-crypto-account-shell { width: 100%; padding: 18px 14px 36px; }
  .add-crypto-account-heading { align-items: flex-start; margin-bottom: 16px; }
  .heading-icon { flex-basis: 40px; width: 40px; height: 40px; }
  .add-crypto-account-heading h2 { font-size: 20px; }
  .add-crypto-account-heading p { font-size: 12px; }
  .crypto-account-panel { padding: 18px 14px 24px; }
  .crypto-account-panel-title { align-items: flex-start; }
  .add-crypto-account-page ::v-deep .btn-account { width: 100% !important; }
}
</style>
